import { Grid, IconButton, LinearProgress, Skeleton, Theme, Typography } from '@mui/material';
import { useHistory, useLocation, useParams } from 'react-router';

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, SxProps } from '@mui/system';

import { ColumnType, DataTable } from '../../../DataTable';
import { SubHeading } from '../../../investors/components';
import {
  additionalKycFieldsMap,
  addressTypeMap,
  APPLICATION_TYPE,
  bankFieldsMap,
  contactFieldsMap,
  contributorFieldsMap,
  distributorFieldsMap,
  documentTypeNameMap,
  fatcaFieldsMap,
  nomineeFieldsMap,
  RISK_CATEGORIZATION,
  RiskCategoryMap,
  USER_ROLES,
} from '../../../../utils/constant';
import {
  autoFormatText,
  displayNctRequestApprovalUI,
  extractAction,
  resolveRiskLabel,
} from '../../../../utils/utilityFunctions';
import { ActionDialog, CommonLayout, RejectApproveButtonsForNCT } from '../../../commonComponents';
import { Formik, FormikProps } from 'formik';
import UseRadioGroup from '../../../../lib/formik/Radio';
import { MFTextField } from '../../../../lib/formik';
import {
  getNctApplicationDetails,
  updateNctApplication,
} from '../../../../redux-store/actions/nct';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { Link as RouterLink } from 'react-router-dom';
import { RootStateType } from '../../../../redux-store/reducers';
import { ApplicationNctProps } from '../../../../redux-store/types/nct';
import { formatShortDateAndTime } from '../../../../utils/date';
import { riskCategoryValidation } from '../../../../utils/schema';
import { BrokerList } from '../../../../redux-store/types/api-types';
import { getBrokers } from '../../../../redux-store/actions/application';

// Field changes — you already have this
const getTableHeader = (): ColumnType[] => [
  { header: 'Field', label: 'field' },
  { header: 'Previous Details', label: 'oldValue' },
  { header: 'Updated Details', label: 'newValue' },
];

// New: entity add/delete table
const getEntityTableHeader = (): ColumnType[] => [
  { header: 'Action', label: 'action' },
  { header: 'Entity Type', label: 'entityType' }, // "Bank Details (Added/Deleted)", "Nominee Details (Added/Deleted)"
  { header: 'Previous Details', label: 'oldValue' },
  { header: 'Updated Details', label: 'newValue' },
];

// Field-change rows (unchanged)
export type DiffRow = {
  field: string;
  oldValue: any;
  newValue: any;
};

// New: entity add/delete rows
export type EntityChangeRow = {
  action: string;
  entityType: string;
  oldValue: any;
  newValue: any;
};

export type FormValuesType = {
  riskCategory: string;
  riskCategoryExplanation: string;
};

export default function UpdatedDetails({ boxStyles }: { boxStyles?: SxProps<Theme> }): JSX.Element {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation<{ id: string; source: string; auditId: string; section: string }>();
  const { source = '', auditId = '', section = '' } = location.state || {};
  const { id } = useParams<{ id: string }>();
  const [application, setApplication] = useState<ApplicationNctProps>();
  const [loading, setLoading] = useState(false);
  const formikRef = useRef<FormikProps<FormValuesType>>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const [riskData, setRiskData] = useState({
    riskCategory: '',
    riskCategoryExplanation: '',
  });
  const [brokerList, setBrokerList] = useState<BrokerList>();

  useEffect(() => {
    (async function () {
      try {
        const response = (await dispatch(getBrokers())) as unknown as BrokerList;
        setBrokerList(response as BrokerList);
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, []);
  const handleDialogOpen = (type: 'approve' | 'reject') => {
    setActionType(type);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setActionType(null);
  };

  const approveHandler = async (formData: FormValuesType) => {
    setApproveLoading(true);
    try {
      await dispatch(
        updateNctApplication({
          body: { ...application, ...formData, approval_status: 'approved', status: 'completed' },
          applicationId: id,
          toastMessage: '',
        })
      );
      history.push('/investors-list');
    } catch (e) {
      console.error((e as Error).message);
    } finally {
      setApproveLoading(false);
    }
  };

  const rejectHandler = async (reason: string, formData: FormValuesType) => {
    setRejectLoading(true);
    try {
      await dispatch(
        updateNctApplication({
          body: {
            ...application,
            ...formData,
            approval_status: 'rejected',
            rejectedReason: reason,
          },
          applicationId: id,
          toastMessage: '',
        })
      );
      history.push('/investors-list');
    } catch (e) {
      console.error((e as Error).message);
    } finally {
      setRejectLoading(false);
    }
  };

  useEffect(() => {
    (async function () {
      try {
        setLoading(true);
        const _application = (await dispatch(
          getNctApplicationDetails(id)
        )) as unknown as ApplicationNctProps;
        setApplication(_application);
        setRiskData({
          riskCategory: _application?.riskCategory,
          riskCategoryExplanation: _application?.riskCategoryExplanation,
        });
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    })();
    setLoading(true);
  }, [id]);

  type SectionBuckets = Record<
    string,
    { fieldChanges: DiffRow[]; entityChanges: EntityChangeRow[] }
  >;

  const ensureSection = (buckets: SectionBuckets, name: string) => {
    if (!buckets[name]) buckets[name] = { fieldChanges: [], entityChanges: [] };
  };

  // Map item to a UI section name (mirror your existing grouping logic)
  const resolveSectionName = (item: any): string => {
    const tn = (item.tableName || '').toLowerCase();
    const changeType = (item.changeType || '').toLowerCase();

    if (tn.includes('bank')) return 'Bank Details';

    if (
      tn.includes('nominees') ||
      (tn.includes('applications') && nomineeFieldsMap[item.fieldName])
    ) {
      return 'Nominee Details'; // 👈 field-level updates
    }

    if (tn.includes('documents')) return 'Document Details';
    if (item.fieldName && addressTypeMap[item.fieldName]) return 'Contact Details';
    if (tn.includes('applicants')) {
      if (item.fieldName && additionalKycFieldsMap[item.fieldName]) return 'Additional Kyc Details';
      if (item.fieldName && fatcaFieldsMap[item.fieldName]) return 'Fatca Details';
      if (item.fieldName && contributorFieldsMap[item.fieldName]) return 'Contributor Details';
      if (item.fieldName && contactFieldsMap[item.fieldName]) return 'Contact Details';
    }
    if (tn.includes('applications')) {
      if (item.fieldName && distributorFieldsMap[item.fieldName]) return 'Distributor Details';
    }
    if (tn.includes('cvl_kra')) return 'CVL KRA Details';
    return 'Other Changes';
  };

  // Use your field maps to label the field column
  const resolveFieldLabel = (item: any): string => {
    const f = item.fieldName;
    if (!f) return '';
    return (
      additionalKycFieldsMap[f] ??
      contactFieldsMap[f] ??
      nomineeFieldsMap[f] ??
      bankFieldsMap[f] ??
      contributorFieldsMap[f] ??
      distributorFieldsMap[f] ??
      fatcaFieldsMap[f] ??
      documentTypeNameMap[f] ??
      RiskCategoryMap[f] ??
      f
    );
  };

  // Robust action detection for entity add/delete
  const detectAction = (item: any): 'ADDED' | 'DELETED' | undefined => {
    if (item.actionType === 'ADDED' || item.actionType === 'DELETED') return item.actionType;
    // Fallback heuristics if backend doesn't send actionType
    const hasOld = item.oldValue !== undefined && item.oldValue !== null && item.oldValue !== '-';
    const hasNew = item.newValue !== undefined && item.newValue !== null && item.newValue !== '-';
    // console.log(item.fieldName, !hasOld && hasNew);
    if (extractAction(item.fieldName).includes('new')) {
      if (!hasOld && hasNew) return 'ADDED';
    }
    if (extractAction(item.fieldName).includes('del')) {
      if (hasOld && !hasNew) return 'DELETED';
    }
    return undefined;
  };

  const buildSectionBuckets = (): SectionBuckets => {
    const buckets: SectionBuckets = {};
    const diffs = auditId
      ? application?.differences?.filter((d: any) => d.auditId === auditId)
      : application?.differences;
    // console.log('diffs', diffs);

    diffs?.forEach((item: any) => {
      const section = resolveSectionName(item);
      // console.log(item, section);
      if (!section) return;
      ensureSection(buckets, section);

      // 1) Field-level updates -> Field table
      if (
        !(
          extractAction(item.fieldName).includes('new') ||
          extractAction(item.fieldName).includes('del')
        )
      ) {
        buckets[section].fieldChanges.push({
          field: resolveFieldLabel(item),
          oldValue: resolveRiskLabel(item.oldValue, item.fieldName, brokerList) ?? '',
          newValue: resolveRiskLabel(item.newValue, item.fieldName, brokerList) ?? '',
        });
        return;
      }

      // 2) Entity-level add/delete -> Entity table
      const action = detectAction(item);
      // console.log('action', action);
      if (action === 'ADDED') {
        buckets[section].entityChanges.push({
          action: action,
          entityType: autoFormatText(item.fieldName),
          oldValue: '-',
          newValue: item.newValue ?? '',
        });
      } else if (action === 'DELETED') {
        buckets[section].entityChanges.push({
          action: action,
          entityType: autoFormatText(item.fieldName),
          oldValue: item.oldValue ?? '',
          newValue: '-',
        });
      }
    });

    return buckets;
  };

  const buckets = buildSectionBuckets();
  // console.log(buckets);

  const noop = () => {
    // Form submission is handled manually using formikRef, not through this handler
  };

  const handleAction = async (action: 'approve' | 'reject') => {
    if (formikRef.current) {
      const errors = await formikRef.current.validateForm();
      if (Object.keys(errors).length === 0) {
        handleDialogOpen(action); // only open dialog if form is valid
      } else {
        formikRef.current.setTouched(
          Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
        );
      }
    }
  };

  const auditLog = application?.audit_logs?.find((log) => log.id === auditId);

  return (
    <CommonLayout>
      <>
        {loading ? (
          <LinearProgress />
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontFamily: 'Poppins, sans-serif',
                fontSize: 18,
                fontWeight: 500,
                mb: { xs: 2, md: 4 },
              }}>
              <IconButton
                sx={{ height: 'fit-content', p: 0 }}
                component={RouterLink}
                to={{
                  pathname:
                    application?.applicationType === APPLICATION_TYPE.INDIVIDUAL
                      ? [USER_ROLES.AMCAPPROVER].includes(role)
                        ? `/investors-list`
                        : source === `/individual-kyc-preview/${id}`
                        ? `/individual-kyc-preview/${id}`
                        : `/individual-kyc-details/${id}`
                      : [USER_ROLES.AMCAPPROVER].includes(role)
                      ? `/investors-list`
                      : source === `/non-individual-kyc-preview/${id}`
                      ? `/non-individual-kyc-preview/${id}`
                      : `/non-individual-kyc-details/${id}`,
                  state: { from: section },
                }}>
                <NavigateBeforeIcon fontSize="large" sx={{ color: 'common.black' }} />
              </IconButton>
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 18,
                  fontWeight: 500,
                  color: 'primary.main',
                }}>
                Updated Details
              </Typography>
            </Box>
            {USER_ROLES.AMCAPPROVER !== role && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 18,
                  fontWeight: 500,
                  mb: { xs: 2, md: 4 },
                  p: { xs: '5px 15px', md: '' },
                  bgcolor: '#e5f1f8',
                  borderRadius: '10px',
                }}>
                <Typography
                  sx={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 18,
                    fontWeight: 500,
                    color: 'primary.main',
                  }}>
                  Modified By: {auditLog?.modified_by}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 18,
                    fontWeight: 500,
                    color: 'primary.main',
                  }}>
                  Modified Date: {formatShortDateAndTime(auditLog?.updatedAt)}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                bgcolor: 'white',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                borderRadius: '10px',
              }}
              mb={3}>
              <Box sx={{ p: 2 }}>
                <Grid container sx={{ mt: 1, pl: { xs: 2, sm: 10 }, pr: { xs: 2, sm: 10 } }}>
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={2}>
                    <Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: 600,
                        color: 'text.secondary',
                      }}>
                      Application ID: {application?.applicationNumber}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {Object.keys(buckets).length === 0 && (
              <Box
                sx={{
                  bgcolor: 'white',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                  borderRadius: '10px',
                  minHeight: 200,
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                mb={3}>
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: 'text.secondary',
                  }}>
                  No Log Data Available
                </Typography>
              </Box>
            )}

            {Object.entries(buckets).map(([sectionName, { fieldChanges, entityChanges }], idx) => (
              <React.Fragment key={idx}>
                {/* <SubHeading sx={{ marginBottom: 1 }}>{sectionName}</SubHeading> */}

                {/* Table A: Field Changes */}
                {fieldChanges.length > 0 && (
                  <>
                    <SubHeading sx={{ marginBottom: 1 }}>{sectionName}</SubHeading>
                    <Box
                      sx={{
                        bgcolor: 'white',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                        borderRadius: '10px',
                        marginBottom: '32px',
                      }}>
                      <DataTable
                        isLoading={loading}
                        tableHeader={getTableHeader()}
                        tableData={fieldChanges}
                        rowCustomStyles={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '.MuiTableCell-body ': { color: '#686e72' },
                          background: 'transparent',
                        }}
                        boxStyles={{
                          minHeight: 'fit-content',
                          boxShadow: '0px 4px 12px rgba(0,0,0,.05)',
                        }}
                      />
                    </Box>
                  </>
                )}

                {/* Table B: Entity Add/Delete */}
                {entityChanges.length > 0 && (
                  <>
                    <SubHeading sx={{ marginBottom: 1 }}>{sectionName} — Entity Changes</SubHeading>
                    <Box
                      sx={{
                        bgcolor: 'white',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                        borderRadius: '10px',
                        marginBottom: '50px',
                      }}>
                      <DataTable
                        isLoading={loading}
                        tableHeader={getEntityTableHeader()}
                        tableData={entityChanges}
                        rowCustomStyles={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '.MuiTableCell-body ': { color: '#686e72' },
                          background: 'transparent',
                        }}
                        boxStyles={{
                          minHeight: 'fit-content',
                          boxShadow: '0px 4px 12px rgba(0,0,0,.05)',
                        }}
                      />
                    </Box>
                  </>
                )}
              </React.Fragment>
            ))}

            {displayNctRequestApprovalUI(role) && (
              <>
                <SubHeading sx={{ marginBottom: '8px' }}>{'Risk Categorization'}</SubHeading>
                <Box
                  sx={{
                    bgcolor: 'white',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                    borderRadius: '10px',
                    py: 5,
                  }}>
                  <Formik<FormValuesType>
                    innerRef={formikRef}
                    initialValues={riskData}
                    validationSchema={riskCategoryValidation}
                    onSubmit={noop} // Not used directly
                  >
                    {({ handleSubmit }) => (
                      <Grid
                        container
                        rowSpacing={1}
                        sx={{
                          width: '80%',
                          mx: 'auto',
                          '.MuiGrid-item': { px: { xs: 0, sm: '30px' } },
                        }}
                        component="form"
                        noValidate
                        onSubmit={handleSubmit}>
                        <Grid item xs={12} sm={12}>
                          <UseRadioGroup
                            formLabel="Risk Category *"
                            name={`riskCategory`}
                            // defaultValue="current"
                            items={Object.keys(RISK_CATEGORIZATION).map((riskCategory) => ({
                              label: RISK_CATEGORIZATION[riskCategory],
                              value: riskCategory,
                            }))}
                          />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                          <MFTextField
                            name={`riskCategoryExplanation`}
                            label={`Please provide a brief explanation for your selected risk category`}
                            placeholder="Type here"
                            multiline
                            rows={4}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Formik>
                </Box>
              </>
            )}
            {/* <DocumentDetails application={application as ApplicationProps} /> */}
            {displayNctRequestApprovalUI(role) && (
              <RejectApproveButtonsForNCT
                handleDialogOpen={handleAction}
                approve={approveLoading}
                reject={rejectLoading}
              />
            )}
          </>
        )}
        <ActionDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          actionType={actionType as 'approve' | 'reject'}
          onSubmit={async (reason) => {
            const formData = formikRef.current?.values;
            if (!formData) return;
            handleDialogClose();
            if (actionType === 'approve') {
              console.log('Calling approveHandler...');
              await approveHandler(formData);
            } else {
              if (typeof reason === 'string') {
                await rejectHandler(reason, formData);
              } else {
                console.error('Rejection reason is missing');
              }
            }
          }}
        />
      </>
    </CommonLayout>
  );
}
